import { useEffect, useMemo, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import clsx from 'clsx';

import {
  hasEarlyAccess,
  PROMO_TYPE_FIELDS,
  PROMO_TYPE_LABEL_IDS,
  PROMO_TYPES,
  ScheduledOfferModel,
  ScheduledOfferPromoType,
  ScheduledOfferState,
} from '../models/ScheduledOfferModel';
import {
  listScheduledOffers,
  toggleScheduledOfferState,
} from '../services/ScheduledOfferCRUD';
import ScheduledOfferModalForm from './ScheduledOfferModalForm';

enum LoadingState {
  IDLE = 'idle',
  LOADING = 'loading',
}

type StateFilter = 'all' | ScheduledOfferState;
type PromoTypeFilter = 'all' | ScheduledOfferPromoType;

function formatDateTime(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDiscount(offer: ScheduledOfferModel): string {
  return offer.discount_mode === 'absolute'
    ? `${offer.discount_value}${offer.currency_symbol}`
    : `${offer.discount_value}%`;
}

/** What the offer is worth, rendered per promo type. */
function formatOfferValue(offer: ScheduledOfferModel, intl: IntlShape): string {
  switch (offer.promo_type) {
    case 'price':
      return `${offer.price ?? ''}${offer.currency_symbol}`;
    case 'free-gift':
      return intl.formatMessage({ id: 'scheduledOffers.free' });
    case 'gift-with-purchase':
      return intl.formatMessage(
        { id: 'scheduledOffers.giftWithPurchaseValue' },
        { minUnits: offer.buy_qty, giftUnits: offer.free_qty }
      );
    case 'product-discount':
      return `-${formatDiscount(offer)}`;
    case 'early-access':
      // Items are always a percentage off.
      return (offer.items ?? [])
        .map((item) => `-${Number(item.discount_value)}%`)
        .join(' · ');
    case 'nth-unit-discount':
      return `${offer.nth_unit}ª -${formatDiscount(offer)}`;
    case 'buy-x-get-y':
      // Retail notation: 2 charged + 1 free reads as "3x2".
      return `${(offer.buy_qty ?? 0) + (offer.free_qty ?? 0)}x${offer.buy_qty}`;
    default:
      return '—';
  }
}

/** Product the row is about: early-access offers list several. */
function getOfferProductName(offer: ScheduledOfferModel): string | null {
  if (offer.promo_type !== 'early-access') return offer.product_name;
  return (offer.items ?? []).map((item) => item.product_name).join(', ');
}

/** Types without a per-user cap (free shipping, early access): a dash. */
function formatMaxUses(offer: ScheduledOfferModel): string {
  // Optional chaining: a type unknown to this CRM build still renders.
  return PROMO_TYPE_FIELDS[offer.promo_type]?.maxUses === false
    ? '—'
    : String(offer.max_uses_per_user);
}

export default function ScheduledOffersTable() {
  const intl = useIntl();
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.LOADING
  );
  const [offers, setOffers] = useState<ScheduledOfferModel[]>([]);
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [promoTypeFilter, setPromoTypeFilter] =
    useState<PromoTypeFilter>('all');
  const [search, setSearch] = useState<string>('');
  const [editingOffer, setEditingOffer] = useState<ScheduledOfferModel | null>(
    null
  );
  const [modalVisible, setModalVisible] = useState<boolean>(false);

  async function fetchOffers() {
    setLoadingState(LoadingState.LOADING);
    try {
      const { data } = await listScheduledOffers();
      setOffers(data);
    } catch (error) {
      console.error('Error fetching scheduled offers:', error);
    } finally {
      setLoadingState(LoadingState.IDLE);
    }
  }

  function openCreateModal() {
    setEditingOffer(null);
    setModalVisible(true);
  }

  function openEditModal(offer: ScheduledOfferModel) {
    setEditingOffer(offer);
    setModalVisible(true);
  }

  function closeModal() {
    setModalVisible(false);
    setEditingOffer(null);
  }

  function onUpdated() {
    closeModal();
    fetchOffers();
  }

  async function onToggleState(offer: ScheduledOfferModel) {
    const nextState: ScheduledOfferState =
      offer.state === 'active' ? 'inactive' : 'active';
    try {
      const { data } = await toggleScheduledOfferState(offer.id, nextState);
      setOffers((prev) =>
        prev.map((item) =>
          item.id === data.id ? { ...item, state: data.state } : item
        )
      );
      toast.success(
        intl.formatMessage({ id: 'scheduledOffers.toggleSuccess' })
      );
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'scheduledOffers.error' }));
    }
  }

  const filteredOffers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return offers.filter((offer) => {
      if (stateFilter !== 'all' && offer.state !== stateFilter) return false;
      if (promoTypeFilter !== 'all' && offer.promo_type !== promoTypeFilter) {
        return false;
      }
      if (!term) return true;
      return (
        offer.name.toLowerCase().includes(term) ||
        // Free-shipping promotions carry no product.
        (getOfferProductName(offer) ?? '').toLowerCase().includes(term) ||
        offer.store_name.toLowerCase().includes(term)
      );
    });
  }, [offers, stateFilter, promoTypeFilter, search]);

  useEffect(() => {
    fetchOffers();
  }, []);

  return (
    <>
      <div className="card p-10 w-100">
        <h1 className="mb-8">
          {intl.formatMessage({ id: 'scheduledOffers.title' })}
        </h1>
        <div className="d-flex gap-4 align-items-end flex-wrap">
          <div className="form-group">
            <label htmlFor="stateFilter" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.state' })}
            </label>
            <select
              id="stateFilter"
              className="form-select"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as StateFilter)}
            >
              <option value="all">
                {intl.formatMessage({ id: 'scheduledOffers.allStates' })}
              </option>
              <option value="active">
                {intl.formatMessage({ id: 'scheduledOffers.active' })}
              </option>
              <option value="inactive">
                {intl.formatMessage({ id: 'scheduledOffers.inactive' })}
              </option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="promoTypeFilter" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.promoType' })}
            </label>
            <select
              id="promoTypeFilter"
              className="form-select"
              value={promoTypeFilter}
              onChange={(e) =>
                setPromoTypeFilter(e.target.value as PromoTypeFilter)
              }
            >
              <option value="all">
                {intl.formatMessage({ id: 'scheduledOffers.allPromoTypes' })}
              </option>
              {PROMO_TYPES.map((type) => (
                <option key={type} value={type}>
                  {intl.formatMessage({ id: PROMO_TYPE_LABEL_IDS[type] })}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group flex-grow-1" style={{ minWidth: '240px' }}>
            <label htmlFor="search" className="form-label">
              {intl.formatMessage({ id: 'scheduledOffers.search' })}
            </label>
            <input
              id="search"
              type="text"
              className="form-control"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'scheduledOffers.search',
              })}
            />
          </div>
          {loadingState === LoadingState.LOADING && (
            <div
              className="spinner-border spinner-border-sm text-primary ms-4"
              role="status"
            />
          )}
          <button className="btn btn-primary ms-auto" onClick={openCreateModal}>
            <span className="fa fa-plus me-2"></span>
            {intl.formatMessage({ id: 'scheduledOffers.add' })}
          </button>
        </div>
        {loadingState === LoadingState.IDLE && filteredOffers.length === 0 && (
          <p className="mt-8">
            {intl.formatMessage({ id: 'scheduledOffers.noOffers' })}
          </p>
        )}
        {filteredOffers.length > 0 && (
          <div className="table-responsive mt-8">
            <table className="table table-striped">
              <thead>
                <tr className="fw-bolder">
                  <th>{intl.formatMessage({ id: 'scheduledOffers.state' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'scheduledOffers.promoType' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'scheduledOffers.name' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'scheduledOffers.product' })}
                  </th>
                  <th>{intl.formatMessage({ id: 'scheduledOffers.store' })}</th>
                  <th>{intl.formatMessage({ id: 'scheduledOffers.price' })}</th>
                  <th>
                    {intl.formatMessage({ id: 'scheduledOffers.validFrom' })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: 'scheduledOffers.validUntil' })}
                  </th>
                  <th>
                    {intl.formatMessage({
                      id: 'scheduledOffers.maxUsesPerUser',
                    })}
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.map((offer) => (
                  <tr
                    key={offer.id}
                    className={clsx({
                      'align-middle': true,
                      'opacity-50': offer.state !== 'active',
                    })}
                  >
                    <td>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={offer.state === 'active'}
                          onChange={() => onToggleState(offer)}
                        />
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-light-primary">
                        {/* Our own label first: promo_type_display comes from
                            the backend untranslated, so the same type read
                            "Descuento en producto" in the form and "Discount
                            on the product" here. */}
                        {PROMO_TYPE_LABEL_IDS[offer.promo_type]
                          ? intl.formatMessage({
                              id: PROMO_TYPE_LABEL_IDS[offer.promo_type],
                            })
                          : offer.promo_type_display}
                      </span>
                      {hasEarlyAccess(offer) && (
                        <span
                          className="badge badge-light-info ms-2"
                          title={intl.formatMessage(
                            { id: 'scheduledOffers.earlyAccessTooltip' },
                            {
                              from: formatDateTime(
                                offer.early_access_from ?? ''
                              ),
                              until: formatDateTime(
                                offer.early_access_until ?? ''
                              ),
                              gift: offer.early_access_gift_product_name ?? '—',
                            }
                          )}
                        >
                          {intl.formatMessage({
                            id: 'scheduledOffers.earlyAccessBadge',
                          })}
                        </span>
                      )}
                    </td>
                    <td>{offer.name}</td>
                    <td>{getOfferProductName(offer) || '—'}</td>
                    <td>{offer.store_name}</td>
                    <td>{formatOfferValue(offer, intl)}</td>
                    <td>{formatDateTime(offer.valid_from)}</td>
                    <td>{formatDateTime(offer.valid_until)}</td>
                    <td>{formatMaxUses(offer)}</td>
                    <td>
                      <button
                        className="btn btn-primary btn-sm px-3 py-2 text-nowrap"
                        onClick={() => openEditModal(offer)}
                      >
                        <span className="fa fa-edit me-2"></span>
                        {intl.formatMessage({
                          id: 'scheduledOffers.editAction',
                        })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ScheduledOfferModalForm
        scheduledOffer={editingOffer}
        visible={modalVisible}
        onClose={closeModal}
        onUpdated={onUpdated}
      />
    </>
  );
}
